import type { AssignmentPlatform } from "refsix-core";
import type { ScrapeResult } from "refsix-core";

/**
 * The protocol between the injected scraper and the app (REFSIX-568).
 *
 * Imported by both sides — `src/injected/assignmentsScraper.ts`, which is
 * bundled separately and runs inside the platform's page, and
 * `inAppAssignmentImport.ts`, which runs in the app — so the two cannot drift.
 *
 * Everything crossing this boundary comes out of a page we do not control, so
 * the app treats it as untrusted input and reads it through `parseScrapeMessage`
 * rather than casting.
 */

/** Namespaced, because any script on the page could post to the same bridge. */
export const SCRAPE_MESSAGE = "refsix/inapp-scrape";

export interface ScrapeMessage {
    type: typeof SCRAPE_MESSAGE;
    /** The URL the scrape ran against. */
    url: string;
    /** Absent only when the scrape threw before producing a result. */
    result?: ScrapeResult;
    /** The thrown error, when it did. */
    error?: string;
}

/**
 * A message off the WebView bridge, or undefined for anything else.
 *
 * Checks only what the app branches on: the discriminator, and that a `result`
 * carries a status. The individual fixtures are validated by refauth's
 * AssignmentsService, which is the one place that has to be strict about them —
 * repeating that here would only mean two rulesets to keep in step.
 */
export function parseScrapeMessage(detail: unknown): ScrapeMessage | undefined {
    if (!detail || typeof detail !== "object") {
        return undefined;
    }

    const candidate = detail as Partial<ScrapeMessage>;
    if (candidate.type !== SCRAPE_MESSAGE) {
        return undefined;
    }

    const result =
        candidate.result && typeof candidate.result.status === "string"
            ? candidate.result
            : undefined;

    if (!result && typeof candidate.error !== "string") {
        return undefined;
    }

    return {
        type: SCRAPE_MESSAGE,
        url: typeof candidate.url === "string" ? candidate.url : "",
        result,
        error: typeof candidate.error === "string" ? candidate.error : undefined,
    };
}

/**
 * Does a URL belong to this platform?
 *
 * The catalogue's host patterns are manifest match patterns ("https://*.host/*"),
 * so `*` before the host means "any subdomain" and the path is ignored — the
 * question here is only which site the page was on.
 */
export function isPlatformUrl(
    url: string,
    platform: Pick<AssignmentPlatform, "hostPatterns">
): boolean {
    let host: string;
    try {
        const parsed = new URL(url);
        // http would mean the session travelled in clear text; no platform we
        // support serves its schedule that way.
        if (parsed.protocol !== "https:") {
            return false;
        }
        host = parsed.hostname.toLowerCase();
    } catch (err) {
        console.warn("Could not parse the WebView URL", url, err);
        return false;
    }

    return platform.hostPatterns.some((pattern) => {
        const patternHost = pattern
            .replace(/^https:\/\//, "")
            .replace(/\/.*$/, "")
            .toLowerCase();

        if (patternHost.startsWith("*.")) {
            const domain = patternHost.slice(2);
            return host === domain || host.endsWith(`.${domain}`);
        }

        return host === patternHost;
    });
}

/**
 * Whether the WebView should be sent to the platform's schedule page.
 *
 * Signing in does not necessarily return the user to the page we asked for.
 * Arbiter's Azure AD B2C flow drops them on their own dashboard, which does not
 * carry the fixture table — so without this the import would wait out its
 * ten-minute timeout on a page that can never parse, and the WebView has no
 * address bar for the user to correct it.
 *
 * The test is **same host, different path**, and that host check is what keeps
 * this away from the sign-in flow: Arbiter authenticates on
 * `go.arbitersports.com`, which is inside its host patterns but is not where the
 * schedule lives. Navigating away from there would break the login itself.
 *
 * Callers must also do this at most once per import — a schedule page that
 * genuinely has nothing on it never parses either, and would otherwise be
 * reloaded forever.
 */
export function shouldRedirectToSchedule(
    currentUrl: string,
    scheduleUrl: string
): boolean {
    try {
        const current = new URL(currentUrl);
        const schedule = new URL(scheduleUrl);
        return (
            current.host.toLowerCase() === schedule.host.toLowerCase() &&
            current.pathname.toLowerCase() !== schedule.pathname.toLowerCase()
        );
    } catch (err) {
        console.warn(
            "Could not compare the WebView URL with the schedule URL",
            currentUrl,
            err
        );
        return false;
    }
}

/**
 * What the app should do about a message.
 *
 * The important case is `waiting`. The scraper runs on every page the WebView
 * loads, which includes the platform's login page, its landing page and any
 * interstitial on the way — so most messages during a normal import say "no
 * adapter recognised this page". That is the user still signing in, not a
 * failure, and reporting it as one would abandon the import the moment it
 * started. Only a page an adapter actually claimed can succeed or fail.
 */
export type ScrapeDisposition =
    /** Not a schedule page. Keep the WebView open and wait for the user. */
    | { act: "waiting"; diagnostics?: string }
    /** A schedule page, read successfully. Import it. */
    | { act: "import"; result: Extract<ScrapeResult, { status: "ok" }> }
    /** A schedule page we could not turn into an import. Tell the user. */
    | {
          act: "failed";
          reason: "noFixtures" | "allInPast" | "tooMany" | "error";
          found?: number;
          diagnostics?: string;
      };

export function scrapeDisposition(message: ScrapeMessage): ScrapeDisposition {
    if (!message.result) {
        return { act: "failed", reason: "error", diagnostics: message.error };
    }

    const result = message.result;

    switch (result.status) {
        case "ok":
            return { act: "import", result };

        case "noTable":
            return { act: "waiting", diagnostics: result.diagnostics };

        case "noFixtures":
            // The page parsed; there was simply nothing upcoming on it. Distinct
            // from "unreadable", because it sends the user somewhere different:
            // to their own schedule filters rather than to support.
            return {
                act: "failed",
                reason: result.allInPast ? "allInPast" : "noFixtures",
                diagnostics: result.diagnostics,
            };

        case "tooMany":
            return { act: "failed", reason: "tooMany", found: result.found };
    }
}
